import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import reportAPI from '../services/report.api.js';

const useReportStore = create(
  persist(
    (set, get) => ({
      // State
      reports: [],
      userReports: [],
      reportReasons: [],
      reportStats: null,
      selectedReport: null,

      // Pagination
      reportsPagination: null,
      userReportsPagination: null,

      // UI State
      isLoading: false,
      error: null,
      showReportModal: false,
      reportTarget: null, // { type: 'post'|'comment'|'user', id: string, data: any }

      // =================== REPORT ACTIONS ===================
      createReport: async (data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await reportAPI.createReport(data);

          // Add to user reports
          set(state => ({
            userReports: [response.data, ...state.userReports],
            showReportModal: false,
            reportTarget: null,
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getUserReports: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await reportAPI.getUserReports(params);

          set({
            userReports: response.data.reports,
            userReportsPagination: response.data.pagination,
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get user reports error:', message);
          set({ error: message });
          return {
            reports: [],
            pagination: null,
          };
        } finally {
          set({ isLoading: false });
        }
      },

      getReportReasons: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await reportAPI.getReportReasons();

          set({ reportReasons: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get report reasons error:', message);
          set({ error: message });
          return [];
        } finally {
          set({ isLoading: false });
        }
      },

      // Admin actions
      getReports: async (params = {}) => {
        set({ isLoading: true, error: null });
        try {
          const response = await reportAPI.getReports(params);

          set({
            reports: response.data.reports,
            reportsPagination: response.data.pagination,
          });

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      getReportStats: async () => {
        set({ isLoading: true, error: null });
        try {
          const response = await reportAPI.getReportStats();

          set({ reportStats: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          console.error('Get report stats error:', message);
          set({ error: message });
          return null;
        } finally {
          set({ isLoading: false });
        }
      },

      getReportById: async (reportId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await reportAPI.getReportById(reportId);

          set({ selectedReport: response.data });
          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      updateReportStatus: async (reportId, data) => {
        set({ isLoading: true, error: null });
        try {
          const response = await reportAPI.updateReportStatus(reportId, data);

          // Update in reports list
          set(state => ({
            reports: state.reports.map(report =>
              report.id === reportId ? response.data : report
            ),
            selectedReport: response.data,
          }));

          return response.data;
        } catch (error) {
          const message =
            error.friendlyMessage ||
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message;
          set({ error: message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      // =================== UI ACTIONS ===================
      setShowReportModal: (show) => set({ showReportModal: show }),

      setReportTarget: (target) => set({ reportTarget: target }),

      openReportModal: (target) => {
        set({
          showReportModal: true,
          reportTarget: target
        });

        // Load report reasons if not already loaded
        if (get().reportReasons.length === 0) {
          get().getReportReasons();
        }
      },

      closeReportModal: () => set({
        showReportModal: false,
        reportTarget: null
      }),

      clearError: () => set({ error: null }),
    }),
    {
      name: 'report-storage',
      partialize: (state) => ({
        userReports: state.userReports,
        reportReasons: state.reportReasons,
      }),
    }
  )
);

export default useReportStore;